import React, { useState } from 'react';
import { TextAreaInput } from './TextAreaInput';
import { TextInput } from './TextInput';
import { ReservableQuestionField } from '../../../../../../../shared/types/reservable';
import { notUndefined, onlyUnique } from '../../../../../../../shared/lib/functional';
import { isValidEmail } from '../../../../../../../shared/lib/utils';
import phone from 'phone';
import Checkbox from './Checkbox';
import SignatureAgreement from './SignatureAgreement';

export function validateField(field: ReservableQuestionField, value: string): string[] {
  const fieldType = 'type' in field.settings ? field.settings.type ?? '' : '';

  const formatError: string | undefined = value
    ? fieldType === 'email' && !isValidEmail(value)
      ? 'Please enter a valid email address'
      : fieldType === 'phone' && !phone(value).isValid
      ? 'Please enter a valid phone number'
      : fieldType === 'number' && !/^\d+$/.test(value)
      ? 'Please enter digits only'
      : undefined
    : undefined;

  return [field.required && !value ? 'This field is required' : undefined, formatError].filter(
    notUndefined
  ) as string[];
}

interface FieldFillerItemProps {
  field: ReservableQuestionField;
  value: string;
  onChange: (value: string) => void;
}

export default function FieldFillerItem(props: FieldFillerItemProps) {
  const { field, value, onChange } = props;
  const [touched, setTouched] = useState(value.length > 0);
  const fieldType = 'type' in field.settings ? field.settings.type ?? '' : '';

  const showErrors = touched;
  const errors = validateField(field, value);

  return (
    <div className="w-full grid grid-cols-1 gap-1">
      <label className="font-bold uppercase text-xs">
        {field.label}
        <span className="text-error">{field.required ? '*' : ''}</span>
      </label>
      {field.type === 'input' ? (
        <>
          {['date', 'time', 'number', 'email', 'text', 'phone'].includes(fieldType) && (
            <TextInput
              error={showErrors ? errors : []}
              onBlur={() => setTouched(true)}
              aria-label={field.label}
              type={
                fieldType === 'phone'
                  ? 'tel'
                  : fieldType === 'number'
                  ? 'text'
                  : fieldType || 'text'
              }
              value={value}
              onChange={e => {
                const newVal = e.target.value;
                if (fieldType === 'number' && newVal !== '' && !/^\d+$/.test(newVal)) return;
                onChange(newVal);
              }}
            />
          )}
          {['checkbox', 'radio'].includes(field.settings.type || '') && (
            <>
              {(field.settings.options || []).map((opt, i) => {
                const valueArray = value.split(',').filter(v => !!v);
                const type = field.settings.type;

                return (
                  <Checkbox
                    key={i}
                    type={type}
                    label={opt.name}
                    aria-label={field.label}
                    value={opt.name}
                    onBlur={() => setTouched(true)}
                    checked={type === 'radio' ? value === opt.name : valueArray.includes(opt.name)}
                    onValue={checked => {
                      let newValue = '';

                      if (type === 'radio') {
                        newValue = checked ? opt.name : '';
                      } else {
                        if (checked) {
                          newValue = valueArray.concat([opt.name]).filter(onlyUnique).join(',');
                        } else {
                          newValue = valueArray.filter(v => v !== opt.name).join(',');
                        }
                      }

                      onChange(newValue);
                    }}
                  />
                );
              })}
            </>
          )}
        </>
      ) : field.type === 'textarea' ? (
        <TextAreaInput
          aria-label={field.label}
          value={value}
          error={showErrors ? errors : []}
          onBlur={() => setTouched(true)}
          onChange={e => onChange(e.target.value)}
        />
      ) : field.type === 'signature' ? (
        <SignatureAgreement
          content={field.settings.content}
          checkboxLabel={field.settings.checkboxLabel || 'I agree'}
          checked={value === 'agreed'}
          error={showErrors ? errors : []}
          onBlur={() => setTouched(true)}
          onValue={checked => onChange(checked ? 'agreed' : '')}
        />
      ) : field.type === 'list' ? null : null}
      {field.type === 'input' &&
        ['radio', 'checkbox'].includes(fieldType) &&
        errors.length > 0 &&
        showErrors && <div className="text-error">This field is required</div>}
      {field.sublabel && (
        <div
          className="my-2 pl-3 border-l text-muted html-view"
          dangerouslySetInnerHTML={{ __html: field.sublabel }}
        ></div>
      )}
    </div>
  );
}
